import { AfterViewInit, ChangeDetectorRef, Component, Input, OnDestroy } from "@angular/core";
import { Files } from "src/app/miscellaneous/files";
import { SortBy } from "../mini-card-files/mini-card-files.component";
import { BehaviorSubject, Observable, Subject, takeUntil, tap } from "rxjs";
import { BreakpointObserver, BreakpointState, Breakpoints } from "@angular/cdk/layout";

@Component({
  selector: "app-card-files",
  templateUrl: "./card-files.component.html",
  styleUrls: ["./card-files.component.scss"],
})
export class CardFilesComponent implements AfterViewInit, OnDestroy {
  /**
   * Data da Asset Publisher.
   */
  data!: any;

  /**
   * Le props definite in fase di configurazione.
   */
  props: any;

  /**
   * BehaviorSubject per la lista di file.
   */
  _files$ = new BehaviorSubject<any[]>([]);

  /**
   * Opzioni per il sort.
   */
  sortByOptions = [
    { value: SortBy.ASC, label: "Asc" },
    { value: SortBy.DESC, label: "Desc" },
    { value: SortBy.NO_ORDER, label: "Default" },
  ];

  /**
   * Ordinamento corrente.
   */
  sortBy = SortBy.NO_ORDER;

  /**
   * Array che contiene la lista di file.
   */
  originalFileList!: any[];

  /**
   * Template di visualizzazione.
   */
  viewTemplate!: "grid" | "carousel" | "pagination";

  /**
   * Pagina corrente.
   */
  page = 1;

  /**
   * Elementi nella pagina.
   */
  pageSize = 3;

  /**
   * File paginati.
   */
  paginatedFiles: any[] = [];

  /*
   *  The number of elements to show.
   */
  elementsToShow$ = new BehaviorSubject<number>(5);

  /**
   * Subject per l'OnDestroy del componente.
   */
  private destroy$ = new Subject<void>();

  constructor(
    private _breakPointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
    private file: Files
  ) {}

  /**
   * @inheritdoc
   */

  ngAfterViewInit(): void {
    if (this.data) {
      this.originalFileList = [...this.data.data];
      this._files$.next([...this.originalFileList]);
      this.props = this.data.props;
      this.checkBreakpoint();
      this.viewTemplate = this.props.viewTemplate || "grid";
      // For pagination.
      if (this.viewTemplate === "pagination") {
        this.setPaginatedFiles();
      }
      this.cdr.detectChanges();
    }
  }

  downloadFile(item: any) {
    this.file.download(this.props.endpoint, item.id, item.title);
  }

  showPreview(item: any) {
    this.file.preview(this.props.endpoint, item.id);
  }

  /**
   * Returns title key from props.
   */
  getFileTitle(item: any): string {
    const key = this.props?.keyTitle;

    return item[key];
  }

  /**
   * it streams an observable it contains the filelist.
   */
  get files$(): Observable<any[]> {
    return this._files$.asObservable();
  }

  /**
   * Sort files by title.
   */
  sortFiles(selectedOrder: SortBy): void {
    this.sortBy = selectedOrder;

    const files = this._files$.getValue();
    const sortedFiles =
      this.sortBy !== SortBy.NO_ORDER
        ? files.sort((a, b) => {
            if (this.sortBy === SortBy.ASC) {
              return this.getFileTitle(a).localeCompare(this.getFileTitle(b));
            } else {
              return this.getFileTitle(b).localeCompare(this.getFileTitle(a));
            }
          })
        : [...this.originalFileList];

    this._files$.next([...sortedFiles]);
    this.viewTemplate === "pagination" && this.setPaginatedFiles();
  }

  /**
   * Set pagination.
   */
  setPaginatedFiles(): void {
    const files = this._files$.getValue();
    const startIndex = (this.page - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    this.paginatedFiles = files.slice(startIndex, endIndex);
  }

  /**
   * On page change event.
   */
  onPageChange(page: number): void {
    this.page = page;
    this.setPaginatedFiles();
  }

  /**
   * On page size change event.
   */
  onPageSize(pageSize: number): void {
    this.pageSize = pageSize;
    this.page = 1; // Resetta la pagina corrente a 1
    this.setPaginatedFiles();
  }

  /**
   * Set number of visible item by breakpoints.
   */
  checkBreakpoint(): void {
    const managedSize = [
      Breakpoints.XSmall,
      Breakpoints.Small,
      Breakpoints.Medium,
      Breakpoints.Large,
      Breakpoints.XLarge,
    ];

    this._breakPointObserver
      .observe(managedSize)
      .pipe(
        tap((state: BreakpointState) => {
          let elementsToShow = 5;
          if (state.breakpoints[Breakpoints.XSmall]) {
            elementsToShow = 1;
          } else if (state.breakpoints[Breakpoints.Small]) {
            elementsToShow = 2;
          } else if (
            state.breakpoints[Breakpoints.Medium] ||
            state.breakpoints[Breakpoints.Large] ||
            state.breakpoints[Breakpoints.XLarge]
          ) {
            elementsToShow = 3;
          }
          if (this.props?.elementsToShow && this.props.elementsToShow < elementsToShow) {
            elementsToShow = this.props.elementsToShow;
          }
          this.elementsToShow$.next(elementsToShow);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * @inheritdoc
   */
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
